/**
 * Step 1 — Basic Info + Schedule
 *
 * Title, type, severity, location (country-aware road picker with GPS),
 * description, and the schedule block (planned start/end + traffic impact).
 * Severity is rendered as a `SegmentedControl` with the project severity
 * palette via CSS custom properties.
 */
import { memo, useCallback, useMemo } from 'react';
import { Select, Stack, Text } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { useTranslation } from 'react-i18next';

import { AppTextInput } from '@/components/common/AppTextInput';
import { AppTextarea } from '@/components/common/AppTextarea';
import { ColoredSegmentedControl } from '@/components/common/ColoredSegmentedControl';
import { FormSection } from '@/components/common/form';
import { RoadDisplayField } from '@/components/common/RoadDisplayField';
import { useGPSLocationSuggestion } from '@/hooks/useGPSLocationSuggestion';
import {
  OBSTRUCTION_DESCRIPTION_MAX_LENGTH,
  OBSTRUCTION_TITLE_MAX_LENGTH,
  SEVERITY_COLORS,
} from '@/constants/obstructions';
import { TEXTAREA_MAX_ROWS, TEXTAREA_MIN_ROWS } from '@/constants/ui';
import { ObstructionType, SeverityLevel } from '@/types/obstructions';

import { useObstructionRoadDetection } from '../../hooks/useObstructionRoadDetection';
import type { ObstructionWizardFormState } from '../../types';
import { ScheduleSection } from './parts/ScheduleSection';

import styles from './Step1BasicInfo.module.css';

const SEVERITY_ORDER: readonly SeverityLevel[] = [
  SeverityLevel.Low,
  SeverityLevel.Medium,
  SeverityLevel.High,
  SeverityLevel.Critical,
];

const TYPE_ORDER: readonly ObstructionType[] = [
  ObstructionType.Maintenance,
  ObstructionType.Construction,
  ObstructionType.Accident,
  ObstructionType.Closure,
  ObstructionType.Event,
  ObstructionType.Emergency,
];

interface Step1BasicInfoProps {
  form: UseFormReturnType<ObstructionWizardFormState>;
  isLoading: boolean;
}

export const Step1BasicInfo = memo(function Step1BasicInfo({
  form,
  isLoading,
}: Step1BasicInfoProps) {
  const { t } = useTranslation();

  const gpsLocationSuggestion = useGPSLocationSuggestion();
  const setLocation = useCallback(
    (value: string) => form.setFieldValue('location', value),
    [form]
  );
  const { roadSource, handleRequestGPSDetection, handleManualRoadInput } =
    useObstructionRoadDetection({
      gpsLocationSuggestion,
      setLocation,
    });

  const typeOptions = useMemo(
    () =>
      TYPE_ORDER.map((typeValue) => ({
        value: typeValue,
        label: t(`obstructions.type.${typeValue}`),
      })),
    [t]
  );

  const severityOptions = useMemo(
    () =>
      SEVERITY_ORDER.map((severityValue) => ({
        value: severityValue,
        label: t(`obstructions.severity.${severityValue}`),
      })),
    [t]
  );

  return (
    <Stack gap="lg">
      <FormSection
        title={t('obstructions.wizard.step1.heading')}
        description={t('obstructions.wizard.step1.description')}
      >
        <AppTextInput
          label={t('obstructions.wizard.fields.title')}
          placeholder={t('obstructions.wizard.placeholders.title')}
          required
          disabled={isLoading}
          autoComplete="off"
          mb="lg"
          maxLength={OBSTRUCTION_TITLE_MAX_LENGTH}
          value={form.values.title}
          onChange={(event) =>
            form.setFieldValue('title', event.currentTarget.value)
          }
          error={form.errors.title}
        />

        <Select
          label={t('obstructions.wizard.fields.type')}
          placeholder={t('obstructions.wizard.placeholders.type')}
          data={typeOptions}
          required
          disabled={isLoading}
          allowDeselect={false}
          mb="lg"
          value={form.values.type}
          onChange={(value) => {
            if (value) form.setFieldValue('type', value as ObstructionType);
          }}
          error={form.errors.type}
        />

        <div className={styles.severityField}>
          <Text size="sm" fw={500} mb="xs">
            {t('obstructions.wizard.fields.severity')}{' '}
            <Text span c="red.6">
              *
            </Text>
          </Text>
          <ColoredSegmentedControl
            fullWidth
            data={severityOptions}
            disabled={isLoading}
            value={form.values.severity}
            onChange={(value) =>
              form.setFieldValue('severity', value as SeverityLevel)
            }
            color={SEVERITY_COLORS[form.values.severity]}
          />
          {form.errors.severity && (
            <Text size="xs" c="red.6" mt="xs">
              {form.errors.severity}
            </Text>
          )}
        </div>

        <div className={styles.locationField}>
          <RoadDisplayField
            value={form.values.location}
            onChange={(value) => form.setFieldValue('location', value)}
            onManualRoadInput={handleManualRoadInput}
            onRequestGPSDetection={handleRequestGPSDetection}
            roadSource={roadSource}
            isDetectingGPS={gpsLocationSuggestion.isDetecting}
            gpsError={gpsLocationSuggestion.error}
            disabled={isLoading}
            error={form.errors.location as string | undefined}
          />
        </div>

        <AppTextarea
          label={t('obstructions.wizard.fields.description')}
          placeholder={t('obstructions.wizard.placeholders.description')}
          disabled={isLoading}
          autosize
          minRows={TEXTAREA_MIN_ROWS}
          maxRows={TEXTAREA_MAX_ROWS}
          maxLength={OBSTRUCTION_DESCRIPTION_MAX_LENGTH}
          value={form.values.description}
          onChange={(event) =>
            form.setFieldValue('description', event.currentTarget.value)
          }
          error={form.errors.description}
        />
      </FormSection>

      <ScheduleSection form={form} isLoading={isLoading} />
    </Stack>
  );
});
